import { QuestionDatabaseComponent } from '../../components/question-database/question-database.component';
import { GenerateUsersComponent } from '../../components/generate-users/generate-users.component';
import { TransactionsComponent } from '../../components/transactions/transactions.component';
import { StudentsComponent } from '../../components/students/students.component';
import { ExamsComponent } from '../../components/exams/exams.component';
import { OskComponent } from '../../components/osk/osk.component';
import { IFASAccessRules, IFASAccountMenu, IFASRedirectConfig, IFASSidebarSection, IFASUserRole } from 'fas-admin';
import { OskSubscriptionsComponent } from '../../components/osk-subscriptions/osk-subscriptions.component';
import { OskPaymentSuccessComponent } from '../../components/osk-subscriptions/osk-payment-success/osk-payment-success.component';
import { OskPaymentCancelComponent } from '../../components/osk-subscriptions/osk-payment-cancel/osk-payment-cancel.component';
import { OskSubscriptionListComponent } from '../../components/osk-subscriptions/osk-subscription-list/osk-subscription-list.component';
import { Route } from '@angular/router';
import { ElearningComponent } from '../../components/elearning/elearning.component';
import { StatisticsComponent } from '../../components/statistics/statistics.component';
import { ServerStatusComponent } from '../../components/server-status/server-status.component';
import { ServerLogsComponent } from '../../components/server-logs/server-logs.component';
import { MediaServersComponent } from '../../components/media-servers/media-servers.component';
import { OutageReportsComponent } from '../../components/outage-reports/outage-reports.component';
import { AppVersionsComponent } from '../../components/app-versions/app-versions.component';
import { SupportReportsComponent } from '../../components/support-reports/support-reports.component';

const workerUserGeneratorRole = 'WorkerUserGenerator' as IFASUserRole;
// OSK employees added by the boss ("Osk"); see src/app/shared/helpers/osk-staff-role.ts.
const oskOfficeRole = 'OskOffice' as IFASUserRole;
const oskInstructorRole = 'OskInstructor' as IFASUserRole;
const oskLecturerRole = 'OskLecturer' as IFASUserRole;
import { OskSettingsComponent } from '../../components/osk-settings/osk-settings.component';
import { InternalExamSettingsComponent } from '../../components/osk-settings/internal-exam-settings/internal-exam-settings.component';
import { StationsSettingsComponent } from '../../components/osk-settings/stations-settings/stations-settings.component';
import { PlatformUsersComponent } from '../../components/platform-users/platform-users.component';
import { LectureGroupsComponent } from '../../components/lecture-groups/lecture-groups.component';
import { QuickStartComponent } from '../../components/quick-start/quick-start.component';
import { platformSettingsSections } from '../../components/platform-settings/platform-settings-sections';
import { PrivacySettingsComponent } from '../../components/privacy-settings/privacy-settings.component';
import { AccessPendingComponent } from '../../components/access-pending/access-pending.component';
import { OSK_GUIDE_PATH } from '../services/osk-guide.service';
import { isLocalhost, localhostOnly } from './localhost-only';

/** Consent to analytics (Microsoft Clarity) - under "Moje konto" for everyone, whatever the role. */
const privacyPath = 'privacy';

// fas-admin matches access rules by the exact JWT `Roles` claim; an account without any role
// (e.g. a self-registered OSK awaiting operator approval) carries an empty string there.
const noRole = '' as IFASUserRole;

export const appSpecificRoutesForFASAdmin: Route[] = [
  { path: privacyPath, component: PrivacySettingsComponent },
  { path: 'platform-users', component: PlatformUsersComponent },
  { path: 'access-pending', component: AccessPendingComponent },
  {
    path: 'questions',
    component: QuestionDatabaseComponent,
  },
  {
    path: 'osk',
    component: OskComponent,
  },
  {
    path: 'generate',
    component: GenerateUsersComponent,
  },
  {
    path: 'osk-subscriptions',
    component: OskSubscriptionsComponent,
    children: [
      {
        path: '',
        redirectTo: 'list',
        pathMatch: 'full',
      },
      {
        path: 'list',
        component: OskSubscriptionListComponent,
      },
      {
        path: 'success',
        component: OskPaymentSuccessComponent,
      },
      {
        path: 'cancel',
        component: OskPaymentCancelComponent,
      },
    ],
  },
  {
    path: 'students',
    component: StudentsComponent,
  },
  {
    path: 'lecture-groups',
    component: LectureGroupsComponent,
  },
  // The OSK calendar: driving lessons (the boss, the office, instructors) and lectures (all staff). Loaded on demand
  // together with FullCalendar, which must stay out of the initial bundle. Still under construction: only on
  // localhost (see localhost-only.ts), like its menu entry, its access and its settings tab.
  ...localhostOnly<Route>([
    {
      path: 'calendar',
      loadComponent: () => import('../../components/calendar/calendar.component').then((m) => m.CalendarComponent),
    },
  ]),
  // OSK agreements and courses; loaded on demand together with the agreement editor (Quill).
  {
    path: 'agreements',
    loadComponent: () =>
      import('../../components/agreements/osk-agreements/osk-agreements.component').then((m) => m.OskAgreementsComponent),
  },
  // Platform operator: agreement templates the OSKs copy into their own agreements.
  {
    path: 'agreement-templates',
    loadComponent: () =>
      import('../../components/agreements/agreement-templates/agreement-templates.component').then(
        (m) => m.AgreementTemplatesComponent
      ),
  },
  // Platform operator: the product catalogue (ITBees.Products) - services and goods, prices, product groups.
  {
    path: 'products',
    loadComponent: () => import('../../components/products/products.component').then((m) => m.ProductsComponent),
  },
  // OSK boss: the lecture licence bought from the "Wykłady" product group. Stripe returns to success / cancel;
  // the library guard checks only this parent path.
  {
    path: 'lectures',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../../components/lectures-license/lectures-license.component').then((m) => m.LecturesLicenseComponent),
      },
      {
        path: 'success',
        loadComponent: () =>
          import('../../components/lectures-license/lectures-license.component').then((m) => m.LecturesLicenseComponent),
        data: { payment: 'success' },
      },
      {
        path: 'cancel',
        loadComponent: () =>
          import('../../components/lectures-license/lectures-license.component').then((m) => m.LecturesLicenseComponent),
        data: { payment: 'cancel' },
      },
    ],
  },
  // "Szybkie skróty" sidebar shortcuts: pick a student / a group and go straight to the station dialog.
  {
    path: 'start-exam',
    component: QuickStartComponent,
    data: { quickStart: 'exam' },
  },
  {
    path: 'start-lectures',
    component: QuickStartComponent,
    data: { quickStart: 'lectures' },
  },
  {
    path: 'exams',
    component: ExamsComponent,
  },
  // OSK employees - the boss only. Loaded on demand, like the other newer pages.
  {
    path: 'employees',
    loadComponent: () =>
      import('../../components/employees/employees.component').then((m) => m.EmployeesComponent),
  },
  // The panel guide for the OSK owner, opened from the "Instrukcja" button in the top bar (OskGuideTopBarService).
  // Loaded on demand: the page brings the guide text; its pictures are assets loaded while scrolling.
  {
    path: OSK_GUIDE_PATH,
    loadComponent: () => import('../../components/osk-guide/osk-guide.component').then((m) => m.OskGuideComponent),
  },
  {
    path: 'transactions',
    component: TransactionsComponent,
  },
  {
    path: 'elearnign',
    component: ElearningComponent,
  },
  {
    path: 'statistics',
    component: StatisticsComponent,
  },
  {
    path: 'osk-statistics',
    loadComponent: () =>
      import('../../components/osk-statistics/osk-statistics.component').then((m) => m.OskStatisticsComponent),
  },
  {
    path: 'server',
    component: ServerStatusComponent,
  },
  {
    path: 'server-logs',
    component: ServerLogsComponent,
  },
  {
    path: 'media-servers',
    component: MediaServersComponent,
  },
  {
    path: 'support-reports',
    component: SupportReportsComponent,
  },
  {
    path: 'outage-reports',
    component: OutageReportsComponent,
  },
  {
    path: 'app-versions',
    component: AppVersionsComponent,
  },
  // "Ustawienia Platformy" (platform operator only, see appAccess): the library guard checks only this parent
  // path, the sub-sections (platformSettingsSections) are its children. Loaded on demand, so the OSK panel
  // does not download it.
  {
    path: 'platform-settings',
    loadComponent: () =>
      import('../../components/platform-settings/platform-settings.component').then((m) => m.PlatformSettingsComponent),
    children: [
      {
        path: '',
        redirectTo: 'integrations',
        pathMatch: 'full',
      },
      // dzielnik.com (invoices for the Stripe payments), InPost ShipX (shipments API) and DeepL (translations).
      {
        path: 'integrations',
        loadComponent: () =>
          import('../../components/platform-settings/integrations/integrations.component').then(
            (m) => m.IntegrationsComponent
          ),
      },
    ],
  },
  // "Ustawienia OSK": the library guard checks only this parent path, the tabs are its children.
  {
    path: 'osk-settings',
    component: OskSettingsComponent,
    children: [
      {
        path: '',
        // The business card is the first tab.
        redirectTo: 'business-card',
        pathMatch: 'full',
      },
      // Loaded on demand: the page brings its form, the card preview and the card's own fonts.
      {
        path: 'business-card',
        loadComponent: () =>
          import('../../components/business-card/business-card.component').then((m) => m.BusinessCardComponent),
      },
      {
        path: 'internal-exam',
        component: InternalExamSettingsComponent,
      },
      // Pairing exam stations with a code; the QR code on a station opens it with ?code=.
      {
        path: 'stations',
        component: StationsSettingsComponent,
      },
      // "Kalendarz i jazdy": the driving policy of the OSK and the student's tab in the app; loaded on demand.
      // Still under construction: only on localhost, like its tab.
      ...localhostOnly<Route>([
        {
          path: 'calendar',
          loadComponent: () =>
            import('../../components/osk-settings/calendar-settings/calendar-settings.component').then(
              (m) => m.CalendarSettingsComponent
            ),
        },
      ]),
    ],
  },
];

export const appSpecificButtonListForFASAdmin = [
  { label: 'Użytkownicy', icon: 'pi pi-id-card', routerUrl: 'platform-users' },
  {
    label: 'Baza pytań',
    icon: 'pi pi-book',
    routerUrl: 'questions',
  },
  {
    label: 'Lista OSK',
    icon: 'pi pi-car',
    routerUrl: 'osk',
  },
  {
    label: 'Wzorce umów',
    icon: 'pi pi-file-edit',
    routerUrl: 'agreement-templates',
  },
  {
    label: 'Produkty',
    icon: 'pi pi-shopping-bag',
    routerUrl: 'products',
  },
  {
    label: 'Generowanie użytkowników',
    icon: 'pi pi-user-plus',
    routerUrl: 'generate',
  },
  {
    label: 'Abonamenty',
    icon: 'pi pi-book',
    routerUrl: 'osk-subscriptions',
  },
  {
    label: 'Kursanci',
    icon: 'pi pi-users',
    routerUrl: 'students',
  },
  {
    label: 'Grupy wykładowe',
    icon: 'pi pi-users',
    routerUrl: 'lecture-groups',
  },
  // Still under construction: only on localhost (see localhost-only.ts).
  ...localhostOnly([{ label: 'Kalendarz', icon: 'pi pi-calendar', routerUrl: 'calendar' }]),
  {
    label: 'Wykłady',
    icon: 'pi pi-video',
    routerUrl: 'lectures',
  },
  {
    label: 'Umowy i kursy',
    icon: 'pi pi-file-edit',
    routerUrl: 'agreements',
  },
  {
    label: 'Egzaminy',
    icon: 'pi pi-book',
    routerUrl: 'exams',
  },
  {
    label: 'Pracownicy',
    icon: 'pi pi-briefcase',
    routerUrl: 'employees',
  },
  {
    label: 'Ustawienia OSK',
    icon: 'pi pi-cog',
    routerUrl: 'osk-settings',
  },
  {
    label: 'Transakcje',
    icon: 'pi pi-credit-card',
    routerUrl: 'transactions',
  },
  {
    label: 'E-learnign',
    icon: 'pi pi-book',
    routerUrl: 'elearnign',
  },
  {
    label: 'Statystyki',
    icon: 'pi pi-chart-line',
    routerUrl: 'statistics',
  },
  {
    label: 'Statystyki OSK',
    icon: 'pi pi-chart-bar',
    routerUrl: 'osk-statistics',
  },
  {
    label: 'Serwer',
    icon: 'pi pi-desktop',
    routerUrl: 'server',
  },
  {
    label: 'Logi Serwera',
    icon: 'pi pi-database',
    routerUrl: 'server-logs',
  },
  {
    label: 'Serwery multimediów',
    icon: 'pi pi-sitemap',
    routerUrl: 'media-servers',
  },
  {
    label: 'Zgłoszenia / Opinie',
    icon: 'pi pi-flag',
    routerUrl: 'support-reports',
  },
  {
    label: 'Awarie',
    icon: 'pi pi-exclamation-triangle',
    routerUrl: 'outage-reports',
  },
  {
    label: 'Wersje aplikacji',
    icon: 'pi pi-mobile',
    routerUrl: 'app-versions',
  },
];

// Titled sections below the main menu.
export const appSidebarSectionsForFASAdmin: IFASSidebarSection[] = [
  // Platform operator: a link to every sub-section of "Ustawienia Platformy".
  {
    title: 'Ustawienia Platformy',
    allowedRoles: ['PlatformOperator'],
    buttons: platformSettingsSections.map((section) => ({
      label: section.label,
      icon: section.icon,
      routerUrl: `platform-settings/${section.path}`,
    })),
  },
  // Quick actions under the OSK menu: pick a student / a group and open the station selection right away.
  {
    title: 'Szybkie skróty',
    allowedRoles: ['Osk'],
    buttons: [
      { label: 'Rozpocznij egzamin', icon: 'pi pi-pencil', routerUrl: 'start-exam' },
      { label: 'Rozpocznij wykłady', icon: 'pi pi-desktop', routerUrl: 'start-lectures' },
    ],
  },
  // Instructors and lecturers run lectures only - internal exams stay with the boss.
  {
    title: 'Szybkie skróty',
    allowedRoles: [oskInstructorRole, oskLecturerRole],
    buttons: [{ label: 'Rozpocznij wykłady', icon: 'pi pi-desktop', routerUrl: 'start-lectures' }],
  },
  // Placeholder "coming soon" section; the button becomes a real link once PKK ships.
  {
    title: 'Wkrótce!',
    allowedRoles: ['Osk'],
    buttons: [{ label: 'PKK', icon: 'pi pi-id-card', disabled: true }],
  },
];

export const appAccess: IFASAccessRules[] = [
  {
    role: 'PlatformOperator',
    access: [privacyPath, 'platform-users', 'subscriptions', 'questions', 'osk', 'agreement-templates', 'products', 'generate', 'elearnign', 'statistics', 'osk-statistics', 'faq', 'support-reports', 'support-settings', 'server', 'server-logs', 'media-servers', 'outage-reports', 'app-versions', 'platform-settings'],
  },
  {
    role: 'Osk',
    access: [
      privacyPath,
      'osk-subscriptions',
      'students',
      'lecture-groups',
      // The calendar is still under construction: only on localhost (see localhost-only.ts), for every OSK role.
      ...localhostOnly(['calendar']),
      'lectures',
      'agreements',
      'start-exam',
      'start-lectures',
      'exams',
      'employees',
      'osk-settings',
      OSK_GUIDE_PATH,
    ],
  },
  // Office: students (adding, data, access), lecture groups, the calendar, agreements and courses.
  {
    role: oskOfficeRole,
    access: [privacyPath, 'students', 'lecture-groups', ...localhostOnly(['calendar']), 'agreements'],
  },
  // Instructor and lecturer: their own students and groups, lectures on the stations, the calendar (an instructor
  // keeps their driving lessons there, a lecturer plans the lectures of their groups).
  {
    role: oskInstructorRole,
    access: [privacyPath, 'students', 'lecture-groups', ...localhostOnly(['calendar']), 'start-lectures'],
  },
  {
    role: oskLecturerRole,
    access: [privacyPath, 'students', 'lecture-groups', ...localhostOnly(['calendar']), 'start-lectures'],
  },
  {
    role: workerUserGeneratorRole,
    access: [privacyPath, 'generate'],
  },
  {
    role: noRole,
    access: [privacyPath, 'access-pending'],
  },
];

export const appRedirectForFASAdmin: IFASRedirectConfig[] = [
  {
    role: 'Osk',
    redirectPath: 'students',
  },
  {
    role: 'PlatformOperator',
    redirectPath: 'platform-users',
  },
  {
    role: workerUserGeneratorRole,
    redirectPath: 'generate',
  },
  { role: oskOfficeRole, redirectPath: 'students' },
  // An instructor starts the day with their calendar ("Mój dzień" on a phone) - once it is out of localhost.
  { role: oskInstructorRole, redirectPath: isLocalhost() ? 'calendar' : 'students' },
  { role: oskLecturerRole, redirectPath: 'students' },
  {
    role: noRole,
    redirectPath: 'access-pending',
  },
];

// "Moje konto" menu in the top bar. The OSK entries (business card, internal exam settings) moved to the
// "Ustawienia OSK" sidebar tab; only the privacy page stays here, as it concerns every role.
export const appAccountMenuButtonsForFASAdmin: IFASAccountMenu[] = [
  {
    icon: 'pi pi-shield',
    label: 'Prywatność',
    path: privacyPath,
  },
];
